import React, { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaScroll } from 'react-icons/fa'
import { useSettingsStore } from '@renderer/stores/settingsStore'

const API_BASE_URL =
  (typeof process !== 'undefined' ? process.env?.VITE_API_URL : undefined) ||
  import.meta.env.VITE_API_URL ||
  'https://dreamboothstudio.com'

/** Warn this many prints before the roll runs out, unless the account set its own. */
const DEFAULT_LOW_AT = 40

/** What GET/PUT /api/paper-stock/:deviceId answers (the parts this row reads). */
interface PaperStock {
  configured: boolean
  initialStock?: number
  estimatedRemaining?: number
  isLowStock?: boolean
  stockSetAt?: string
  alertThreshold?: number
  alertThresholdType?: 'absolute' | 'percent'
}

/**
 * Turns a failed request into one line staff can act on. The status code stays
 * in the text so a screenshot sent to the group says what went wrong.
 */
function failureLine(status: number | null, t: (k: string, d: string) => string): string {
  if (status === 401) {
    return t('settings.paperRoll.errorAuth', 'Login expired (401). Log out, log in again, then retry.')
  }
  if (status === 404) {
    return t(
      'settings.paperRoll.errorDevice',
      'This booth is not registered yet (404). Wait a few minutes after opening the app, then retry.'
    )
  }
  if (status === null) {
    return t('settings.paperRoll.errorNetwork', 'No connection. Check the internet, then retry.')
  }
  return `${t('settings.paperRoll.errorGeneric', 'Could not save')} (${status}).`
}

/**
 * "Paper": how many prints are left on the roll, and a way to say "I just
 * loaded a new roll, the printer shows N left".
 *
 * Staff are at the booth when they change the roll, so this is where the count
 * is entered. The server counts successful prints down from it and pages the
 * owner before the roll runs out (paid sessions used to end with no strip when
 * the ribbon ran out mid-shift).
 */
export function PaperRollRow({ compact }: { compact: boolean }): JSX.Element | null {
  const { t, i18n } = useTranslation()
  const deviceId = useSettingsStore((s) => s.deviceId)
  const [stock, setStock] = useState<PaperStock | null>(null)
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const url = deviceId ? `${API_BASE_URL}/api/paper-stock/${encodeURIComponent(deviceId)}` : null

  const load = useCallback(async (): Promise<void> => {
    if (!url) return
    try {
      const res = await fetch(url, { credentials: 'include' })
      if (!res.ok) {
        console.warn('[PaperRoll] read failed', res.status)
        return
      }
      setStock((await res.json()) as PaperStock)
    } catch (err) {
      console.warn('[PaperRoll] read failed', err)
    }
  }, [url])

  useEffect(() => {
    void load()
  }, [load])

  const save = async (): Promise<void> => {
    if (!url) return
    const count = Math.round(Number(value))
    if (!Number.isFinite(count) || count <= 0) {
      setError(t('settings.paperRoll.errorCount', 'Enter the number the printer shows.'))
      return
    }
    setSaving(true)
    setError(null)
    let status: number | null = null
    try {
      const res = await fetch(url, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          initialStock: count,
          // Keep a threshold the account already chose; otherwise warn at 40 prints.
          alertThreshold: stock?.configured ? stock.alertThreshold : DEFAULT_LOW_AT,
          alertThresholdType: stock?.configured ? stock.alertThresholdType : 'absolute',
          // A new roll: count down from now, even if it is the same size as the last.
          resetUsage: true
        })
      })
      status = res.status
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        console.error('[PaperRoll] save failed', res.status, body)
        setError(failureLine(status, t))
        return
      }
      setStock((await res.json()) as PaperStock)
      setEditing(false)
      setValue('')
    } catch (err) {
      console.error('[PaperRoll] save failed', err)
      setError(failureLine(status, t))
    } finally {
      setSaving(false)
    }
  }

  if (!deviceId) return null

  const textSize = compact ? 'text-xs' : 'text-sm'
  const since = stock?.stockSetAt
    ? // The booth's language, not the PC's locale: "18 Okt" on an Indonesian booth.
      new Date(stock.stockSetAt).toLocaleDateString(i18n.language || undefined, { day: 'numeric', month: 'short' })
    : null

  let summary: React.ReactNode
  if (!stock) {
    summary = '…'
  } else if (!stock.configured) {
    summary = t('settings.paperRoll.notSet', 'Not set. Enter the count when you load a roll.')
  } else {
    summary = (
      <span className={stock.isLowStock ? 'font-medium text-red-600' : undefined}>
        {t('settings.paperRoll.remaining', '~{{left}} of {{total}} left', {
          left: stock.estimatedRemaining ?? 0,
          total: stock.initialStock ?? 0
        })}
        {since ? ` · ${t('settings.paperRoll.loaded', 'loaded {{date}}', { date: since })}` : ''}
      </span>
    )
  }

  return (
    <div className="py-2" data-testid="paper-roll-row">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <FaScroll className="shrink-0 text-gray-500" />
          <div className="min-w-0">
            <div className={textSize}>{t('settings.paperRoll.title', 'Paper')}</div>
            <div className="text-xs text-gray-500">{summary}</div>
          </div>
        </div>
        {!editing && (
          <button
            type="button"
            className={`shrink-0 rounded-md border border-gray-300 px-3 py-1.5 ${textSize} hover:bg-gray-50`}
            onClick={() => {
              setEditing(true)
              setError(null)
            }}
          >
            {t('settings.paperRoll.newRoll', 'New roll')}
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number"
            inputMode="numeric"
            min={1}
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={t('settings.paperRoll.countPlaceholder', 'Left on printer')}
            aria-label={t('settings.paperRoll.countPlaceholder', 'Left on printer')}
            className={`w-36 rounded-md border border-gray-300 px-2 py-1.5 ${textSize}`}
          />
          <button
            type="button"
            disabled={saving}
            onClick={() => void save()}
            className={`rounded-md bg-gray-900 px-3 py-1.5 text-white disabled:opacity-50 ${textSize}`}
          >
            {saving ? '…' : t('common.save', 'Save')}
          </button>
          <button
            type="button"
            disabled={saving}
            onClick={() => {
              setEditing(false)
              setError(null)
            }}
            className={`px-2 py-1.5 text-gray-500 ${textSize}`}
          >
            {t('common.cancel', 'Cancel')}
          </button>
        </div>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  )
}
